/**
 * Multi-domain observability mock corpus for Wecrew Copilot AI demo.
 * Domains: application · devops · platform · sre
 *
 * Limitation: all data is static mock telemetry — connect a log API for production.
 */

export const LOGS_DOMAIN_KEY = "argusai-logs-domain";

export const OBS_DOMAINS = [
  { id: "application", label: "Application", icon: "📱", hint: "API · frontend · workers" },
  { id: "devops", label: "DevOps", icon: "🔧", hint: "CI/CD · deploy · helm/kubectl" },
  { id: "platform", label: "Platform", icon: "☸️", hint: "K3s · Cilium · Wecrew platform stack" },
  { id: "sre", label: "SRE", icon: "🚨", hint: "Incidents · error budget · burn" },
];

const DOMAIN_COLORS = {
  application: { api: "#2563EB", frontend: "#0EA5E9", postgres: "#7C3AED", worker: "#059669", Error: "#DC2626" },
  devops: { deploy: "#2563EB", pipeline: "#7C3AED", helm: "#0891B2", kubectl: "#059669", Error: "#DC2626" },
  platform: { k3s: "#2563EB", cilium: "#7C3AED", gateway: "#0891B2", infisical: "#F59E0B", alloy: "#059669", Error: "#DC2626" },
  sre: { critical: "#DC2626", high: "#EA580C", medium: "#F59E0B", incident: "#7C3AED", Error: "#991B1B" },
};

function mkEvent(base) {
  return {
    logtype: "json",
    status_code: null,
    incident_id: null,
    severity: null,
    ...base,
    timestamp: base.ts?.slice(0, 19),
    metadata: {
      domain: base.domain,
      tag: base.tag,
      logtype: base.logtype || "json",
      host: base.host,
      pod: base.pod,
      trace_id: base.trace_id,
      status_code: base.status_code ?? null,
      incident_id: base.incident_id ?? null,
    },
  };
}

const APPLICATION_RAW = [
  {
    id: 1,
    ts: "2026-01-20 17:40:38.147",
    level: "INFO",
    logger: "com.argusai.api.ChatController",
    thread: "http-nio-8080-exec-12",
    message: "Response status: 200 OK for /api/v1/chat/query",
    tag: "api",
    service: "argusai-api",
    host: "ip-10-0-12-44",
    pod: "argusai-api-02-x3k2m",
    trace_id: "tr_00000412a1b2c3d4",
    status_code: 200,
  },
  {
    id: 2,
    ts: "2026-01-20 17:40:22.891",
    level: "ERROR",
    logger: "com.argusai.api.PoolManager",
    thread: "http-nio-8080-exec-8",
    message: "Connection pool exhausted — max 100 reached",
    tag: "api",
    service: "argusai-api",
    host: "ip-10-0-12-44",
    pod: "argusai-api-01-x1k2m",
    trace_id: "tr_00000412b8e9f0a1",
    status_code: 503,
  },
  {
    id: 3,
    ts: "2026-01-20 17:39:58.412",
    level: "WARN",
    logger: "com.argusai.api.QueryMonitor",
    thread: "http-nio-8080-exec-5",
    message: "Slow query on documents table (842ms)",
    tag: "api",
    service: "argusai-api",
    host: "ip-10-0-12-51",
    pod: "argusai-api-02-x3k2m",
    trace_id: "tr_00000411c2d3e4f5",
    status_code: 200,
  },
  {
    id: 4,
    ts: "2026-01-20 17:39:41.055",
    level: "ERROR",
    logger: "com.argusai.auth.JwtFilter",
    thread: "http-nio-8080-exec-3",
    message: "JWT validation failed for /api/v1/chat/query",
    tag: "api",
    service: "argusai-auth",
    host: "ip-10-0-12-62",
    pod: "argusai-auth-01-x7k2m",
    trace_id: "tr_00000411d4e5f6a7",
    status_code: 401,
  },
  {
    id: 5,
    ts: "2026-01-20 17:39:12.733",
    level: "INFO",
    logger: "com.argusai.rag.IndexWorker",
    thread: "pool-3-thread-2",
    message: "RAG index sync completed — 182 chunks",
    tag: "frontend",
    service: "argusai-rag",
    host: "ip-10-0-12-44",
    pod: "argusai-rag-01-x2k2m",
    trace_id: "tr_00000410e6f7a8b9",
    status_code: 200,
  },
  {
    id: 6,
    ts: "2026-01-20 17:38:55.201",
    level: "INFO",
    logger: "com.argusai.gateway.HealthProbe",
    thread: "http-nio-8080-exec-1",
    message: "Health check passed — API gateway 200",
    tag: "gateway",
    logtype: "apache_access",
    service: "argusai-gateway",
    host: "ip-10-0-12-51",
    pod: "argusai-gateway-01-x4k2m",
    trace_id: "tr_00000410f8a9b0c1",
    status_code: 200,
  },
  {
    id: 7,
    ts: "2026-01-20 17:38:28.664",
    level: "WARN",
    logger: "com.argusai.worker.RetryQueue",
    thread: "pool-2-thread-4",
    message: "Retry queue depth elevated (847 jobs)",
    tag: "worker",
    service: "argusai-worker",
    host: "ip-10-0-12-62",
    pod: "argusai-worker-02-x5k2m",
    trace_id: "tr_0000040fa0b1c2d3",
  },
  {
    id: 8,
    ts: "2026-01-20 17:38:01.990",
    level: "ERROR",
    logger: "com.argusai.api.LlmClient",
    thread: "http-nio-8080-exec-15",
    message: "Upstream LLM API timeout after 30s",
    tag: "api",
    service: "argusai-api",
    host: "ip-10-0-12-44",
    pod: "argusai-api-01-x1k2m",
    trace_id: "tr_0000040fb2c3d4e5",
    status_code: 504,
  },
  {
    id: 9,
    ts: "2026-01-20 17:37:44.318",
    level: "INFO",
    logger: "com.argusai.ui.AppShell",
    thread: "main-thread-1",
    message: "Route transition /dashboard → /chat",
    tag: "frontend",
    service: "argusai-frontend",
    host: "ip-10-0-12-51",
    pod: "argusai-fe-01-x3k2m",
    trace_id: "tr_0000040fc4d5e6f7",
    status_code: 200,
  },
  {
    id: 10,
    ts: "2026-01-20 17:37:18.072",
    level: "ERROR",
    logger: "org.postgresql.jdbc.PgConnection",
    thread: "pool-1-thread-2",
    message: "SQL slow query on embeddings_v2 (842ms)",
    tag: "postgres",
    service: "argusai-api",
    host: "ip-10-0-12-62",
    pod: "argusai-api-02-x3k2m",
    trace_id: "tr_0000040fd6e7f8a9",
    status_code: 500,
  },
];

const DEVOPS_RAW = [
  {
    id: 101,
    ts: "2026-01-20 17:39:02.110",
    level: "INFO",
    logger: "github.actions.Runner",
    thread: "runner-7",
    message: "Workflow deploy-prod completed — argusai-api v3.0.1",
    tag: "deploy",
    service: "github-actions",
    host: "gha-runner-03",
    pod: "gha-runner-03-prod",
    trace_id: "ci_00000401a1b2c3",
    status_code: 200,
  },
  {
    id: 102,
    ts: "2026-01-20 17:38:44.552",
    level: "ERROR",
    logger: "helm.ReleaseManager",
    thread: "helm-1",
    message: "Helm upgrade failed — argusai-api chart: readiness probe timeout",
    tag: "helm",
    service: "helm",
    host: "bastion-01",
    pod: "helm-operator-0",
    trace_id: "ci_00000401b2c3d4",
    status_code: 500,
  },
  {
    id: 103,
    ts: "2026-01-20 17:38:12.881",
    level: "INFO",
    logger: "gitlab.pipeline.Executor",
    thread: "pipeline-12",
    message: "Pipeline #4821 stage build passed — image pushed to ECR",
    tag: "pipeline",
    service: "gitlab-ci",
    host: "gitlab-runner-02",
    pod: "runner-4821-build",
    trace_id: "ci_00000401c3d4e5",
    status_code: 200,
  },
  {
    id: 104,
    ts: "2026-01-20 17:37:55.004",
    level: "WARN",
    logger: "kubectl.RolloutWatcher",
    thread: "kubectl-1",
    message: "Rollout argusai-worker stalled — 2/3 pods ready",
    tag: "kubectl",
    service: "kubectl",
    host: "bastion-01",
    pod: "kubectl-exec",
    trace_id: "ci_00000401d4e5f6",
  },
  {
    id: 105,
    ts: "2026-01-20 17:37:22.331",
    level: "ERROR",
    logger: "github.actions.DeployGate",
    thread: "runner-4",
    message: "Deploy gate failed — smoke test /health returned 503",
    tag: "deploy",
    service: "github-actions",
    host: "gha-runner-01",
    pod: "gha-runner-01-prod",
    trace_id: "ci_00000401e5f6a7",
    status_code: 503,
  },
  {
    id: 106,
    ts: "2026-01-20 17:36:58.772",
    level: "INFO",
    logger: "argocd.SyncController",
    thread: "argocd-2",
    message: "Argo CD sync healthy — wecrew-platform manifest",
    tag: "deploy",
    service: "argocd",
    host: "argocd-server-0",
    pod: "argocd-application-controller-0",
    trace_id: "ci_00000401f6a7b8",
    status_code: 200,
  },
  {
    id: 107,
    ts: "2026-01-20 17:36:30.119",
    level: "WARN",
    logger: "helm.LintRunner",
    thread: "helm-2",
    message: "Chart argusai-gateway: deprecated ingress annotation",
    tag: "helm",
    service: "helm",
    host: "bastion-01",
    pod: "helm-operator-0",
    trace_id: "ci_0000040207b8c9",
  },
  {
    id: 108,
    ts: "2026-01-20 17:35:44.663",
    level: "INFO",
    logger: "kubectl.ApplyRunner",
    thread: "kubectl-3",
    message: "kubectl apply -f k8s/infisical/ — 12 resources unchanged",
    tag: "kubectl",
    service: "kubectl",
    host: "bastion-01",
    pod: "kubectl-exec",
    trace_id: "ci_0000040218c9d0",
    status_code: 200,
  },
];

const PLATFORM_RAW = [
  {
    id: 201,
    ts: "2026-01-20 17:40:10.442",
    level: "INFO",
    logger: "k3s.kubelet.NodeStatus",
    thread: "kubelet-1",
    message: "Node wecrew-worker-03 Ready — all system pods running",
    tag: "k3s",
    service: "k3s",
    host: "wecrew-worker-03",
    pod: "kubelet-wecrew-worker-03",
    trace_id: "plt_00000401a1b2c3",
    status_code: 200,
  },
  {
    id: 202,
    ts: "2026-01-20 17:39:33.881",
    level: "WARN",
    logger: "cilium.agent.PolicyAudit",
    thread: "cilium-1",
    message: "Cilium policy drop — egress to supabase blocked for pod flowise-0",
    tag: "cilium",
    service: "cilium",
    host: "wecrew-worker-01",
    pod: "cilium-agent-abc12",
    trace_id: "plt_00000401b2c3d4",
  },
  {
    id: 203,
    ts: "2026-01-20 17:39:05.220",
    level: "ERROR",
    logger: "gateway.envoy.AccessLog",
    thread: "envoy-2",
    message: "Upstream connect error — argusai-api:8080 connection refused",
    tag: "gateway",
    logtype: "apache_access",
    service: "envoy-gateway",
    host: "wecrew-gateway-01",
    pod: "envoy-gateway-0",
    trace_id: "plt_00000401c3d4e5",
    status_code: 502,
  },
  {
    id: 204,
    ts: "2026-01-20 17:38:40.551",
    level: "INFO",
    logger: "infisical.operator.SecretSync",
    thread: "infisical-1",
    message: "Infisical secret sync OK — argusai/prod refreshed",
    tag: "infisical",
    service: "infisical",
    host: "wecrew-worker-02",
    pod: "infisical-operator-0",
    trace_id: "plt_00000401d4e5f6",
    status_code: 200,
  },
  {
    id: 205,
    ts: "2026-01-20 17:38:15.992",
    level: "ERROR",
    logger: "supabase.rest.Probe",
    thread: "probe-1",
    message: "Supabase REST probe failed — 503 Service Unavailable",
    tag: "supabase",
    service: "supabase",
    host: "wecrew-worker-01",
    pod: "supabase-proxy-0",
    trace_id: "plt_00000401e5f6a7",
    status_code: 503,
  },
  {
    id: 206,
    ts: "2026-01-20 17:37:48.331",
    level: "INFO",
    logger: "flowise.server.WorkflowEngine",
    thread: "flowise-1",
    message: "Flowise chatflow IncidentBot executed — HTTP Request node OK",
    tag: "flowise",
    service: "flowise",
    host: "wecrew-worker-02",
    pod: "flowise-0",
    trace_id: "plt_00000401f6a7b8",
    status_code: 200,
  },
  {
    id: 207,
    ts: "2026-01-20 17:37:20.664",
    level: "WARN",
    logger: "n8n.workflow.ExecutionEngine",
    thread: "n8n-1",
    message: "n8n workflow retry — webhook timeout to Wecrew platform",
    tag: "n8n",
    service: "n8n",
    host: "wecrew-worker-03",
    pod: "n8n-0",
    trace_id: "plt_0000040207b8c9",
  },
  {
    id: 208,
    ts: "2026-01-20 17:36:52.118",
    level: "INFO",
    logger: "platform.alloy.Collector",
    thread: "alloy-1",
    message: "Log pipeline shipped 12.4k log lines to storage",
    tag: "alloy",
    service: "log-collector",
    host: "wecrew-worker-01",
    pod: "alloy-0",
    trace_id: "plt_0000040218c9d0",
    status_code: 200,
  },
  {
    id: 209,
    ts: "2026-01-20 17:36:24.441",
    level: "ERROR",
    logger: "k3s.scheduler.PodEviction",
    thread: "scheduler-1",
    message: "Pod evicted — node wecrew-worker-02 memory pressure",
    tag: "k3s",
    service: "k3s",
    host: "wecrew-worker-02",
    pod: "kube-scheduler-0",
    trace_id: "plt_0000040229d0e1",
    status_code: 500,
  },
];

const SRE_RAW = [
  {
    id: 301,
    ts: "2026-01-20 17:40:05.221",
    level: "ERROR",
    logger: "sre.ErrorBudgetBurn",
    thread: "sre-1",
    message: "Error budget burn rate 4.2x — chat API SLO at risk",
    tag: "incident",
    severity: "critical",
    incident_id: "INC-2841",
    service: "argusai-api",
    host: "sre-controller",
    pod: "sre-budget-watcher-0",
    trace_id: "sre_00000401a1b2c3",
  },
  {
    id: 302,
    ts: "2026-01-20 17:39:18.552",
    level: "WARN",
    logger: "sre.SLOTracker",
    thread: "sre-2",
    message: "Availability SLO 99.2% — below 99.5% target (30d window)",
    tag: "incident",
    severity: "high",
    incident_id: "INC-2839",
    service: "argusai-gateway",
    host: "sre-controller",
    pod: "sre-slo-tracker-0",
    trace_id: "sre_00000401b2c3d4",
  },
  {
    id: 303,
    ts: "2026-01-20 17:38:50.881",
    level: "ERROR",
    logger: "sre.IncidentBridge",
    thread: "sre-3",
    message: "PagerDuty incident opened — pool exhaustion cascade",
    tag: "incident",
    severity: "critical",
    incident_id: "INC-2841",
    service: "argusai-api",
    host: "sre-controller",
    pod: "sre-incident-bridge-0",
    trace_id: "sre_00000401c3d4e5",
  },
  {
    id: 304,
    ts: "2026-01-20 17:38:22.004",
    level: "WARN",
    logger: "sre.DeployCorrelator",
    thread: "sre-4",
    message: "Deploy correlation — errors spiked 12m after argusai-api v3.0.1",
    tag: "incident",
    severity: "medium",
    incident_id: "INC-2840",
    service: "argusai-api",
    host: "sre-controller",
    pod: "sre-deploy-correlator-0",
    trace_id: "sre_00000401d4e5f6",
  },
  {
    id: 305,
    ts: "2026-01-20 17:37:55.331",
    level: "INFO",
    logger: "sre.HealthAggregator",
    thread: "sre-5",
    message: "Service health from logs — 3 failing, 8 healthy",
    tag: "incident",
    severity: "medium",
    service: "sre-platform",
    host: "sre-controller",
    pod: "sre-health-agg-0",
    trace_id: "sre_00000401e5f6a7",
    status_code: 200,
  },
  {
    id: 306,
    ts: "2026-01-20 17:37:28.664",
    level: "ERROR",
    logger: "sre.ErrorBudgetBurn",
    thread: "sre-6",
    message: "Burn alert — 18% monthly error budget consumed in 6h",
    tag: "incident",
    severity: "high",
    incident_id: "INC-2838",
    service: "argusai-rag",
    host: "sre-controller",
    pod: "sre-budget-watcher-0",
    trace_id: "sre_00000401f6a7b8",
  },
];

function generateDomainExtras(domain, startId, count, config) {
  const { tags, loggers, messages, services } = config;
  const levels = ["INFO", "INFO", "WARN", "DEBUG", "ERROR"];
  const out = [];
  for (let i = 0; i < count; i += 1) {
    const id = startId + i;
    const mins = 40 - Math.floor(i / 3);
    const secs = (i * 17) % 60;
    const ms = (i * 137) % 1000;
    const tag = tags[i % tags.length];
    const level = tag === "Error" || tag === "incident" ? "ERROR" : levels[i % levels.length];
    out.push(
      mkEvent({
        id,
        domain,
        ts: `2026-01-20 17:${String(Math.max(mins, 10)).padStart(2, "0")}:${String(secs).padStart(2, "0")}.${String(ms).padStart(3, "0")}`,
        level,
        logger: loggers[tag] || loggers[tags[0]],
        thread: `worker-${(i % 12) + 1}`,
        message: messages[tag]?.replace("{n}", String(200 + (i % 800))) || messages.default,
        tag,
        service: services[tag] || services.default,
        host: `host-${40 + (i % 4)}`,
        pod: `${domain}-${tag}-0${(i % 3) + 1}`,
        trace_id: `${domain.slice(0, 3)}_${String(id).padStart(8, "0")}`,
        status_code: level === "ERROR" ? 500 : 200,
        severity: domain === "sre" ? ["critical", "high", "medium"][i % 3] : null,
        incident_id: domain === "sre" ? `INC-${2830 + (i % 12)}` : null,
      }),
    );
  }
  return out;
}

const APP_EXTRAS = generateDomainExtras("application", 37, 40, {
  tags: ["api", "frontend", "postgres", "worker", "gateway", "Error"],
  loggers: {
    api: "com.argusai.api.ChatController",
    frontend: "com.argusai.ui.AppShell",
    postgres: "org.postgresql.jdbc.PgConnection",
    worker: "com.argusai.worker.JobProcessor",
    gateway: "com.argusai.gateway.EdgeRouter",
    Error: "com.argusai.ops.ErrorReporter",
  },
  messages: {
    api: "Request handled — /api/v1/chat/query",
    frontend: "Route transition /dashboard → /chat",
    postgres: "SQL slow query on embeddings_v2 ({n}ms)",
    worker: "Background job completed — index-queue",
    gateway: "Upstream health probe OK",
    Error: "Unhandled exception in request pipeline",
    default: "Application event",
  },
  services: {
    api: "argusai-api",
    frontend: "argusai-frontend",
    postgres: "argusai-api",
    worker: "argusai-worker",
    gateway: "argusai-gateway",
    Error: "argusai-api",
    default: "argusai-api",
  },
});

const DEVOPS_EXTRAS = generateDomainExtras("devops", 109, 28, {
  tags: ["deploy", "pipeline", "helm", "kubectl", "Error"],
  loggers: {
    deploy: "github.actions.Runner",
    pipeline: "gitlab.pipeline.Executor",
    helm: "helm.ReleaseManager",
    kubectl: "kubectl.RolloutWatcher",
    Error: "github.actions.DeployGate",
  },
  messages: {
    deploy: "Deploy stage completed — revision {n}",
    pipeline: "Pipeline stage passed — build #{n}",
    helm: "Helm release synced — chart revision {n}",
    kubectl: "kubectl rollout status — {n} pods ready",
    Error: "CI/CD gate failed — exit code 1",
    default: "DevOps event",
  },
  services: {
    deploy: "github-actions",
    pipeline: "gitlab-ci",
    helm: "helm",
    kubectl: "kubectl",
    Error: "github-actions",
    default: "github-actions",
  },
});

const PLATFORM_EXTRAS = generateDomainExtras("platform", 210, 32, {
  tags: ["k3s", "cilium", "gateway", "infisical", "supabase", "flowise", "n8n", "alloy", "Error"],
  loggers: {
    k3s: "k3s.kubelet.NodeStatus",
    cilium: "cilium.agent.PolicyAudit",
    gateway: "gateway.envoy.AccessLog",
    infisical: "infisical.operator.SecretSync",
    supabase: "supabase.rest.Probe",
    flowise: "flowise.server.WorkflowEngine",
    n8n: "n8n.workflow.ExecutionEngine",
    alloy: "platform.alloy.Collector",
    Error: "platform.HealthProbe",
  },
  messages: {
    k3s: "K3s node heartbeat OK — CPU {n}%",
    cilium: "Cilium endpoint regenerated — identity {n}",
    gateway: "Envoy route matched — upstream latency {n}ms",
    infisical: "Secret rotated — version {n}",
    supabase: "Supabase connection pool — {n} active",
    flowise: "Flowise execution OK — run {n}",
    n8n: "n8n workflow node completed — step {n}",
    alloy: "Alloy batch shipped — {n} lines",
    Error: "Platform probe failed — dependency down",
    default: "Platform event",
  },
  services: {
    k3s: "k3s",
    cilium: "cilium",
    gateway: "envoy-gateway",
    infisical: "infisical",
    supabase: "supabase",
    flowise: "flowise",
    n8n: "n8n",
    alloy: "log-collector",
    Error: "platform",
    default: "platform",
  },
});

const SRE_EXTRAS = generateDomainExtras("sre", 307, 24, {
  tags: ["incident", "critical", "high", "medium", "Error"],
  loggers: {
    incident: "sre.IncidentBridge",
    critical: "sre.ErrorBudgetBurn",
    high: "sre.SLOTracker",
    medium: "sre.DeployCorrelator",
    Error: "sre.AlertRouter",
  },
  messages: {
    incident: "Incident signal — correlated log spike #{n}",
    critical: "Critical burn — {n}% budget consumed",
    high: "High severity — latency SLO breach {n}ms",
    medium: "Medium — deploy correlation score {n}",
    Error: "SRE alert routed — on-call paged",
    default: "SRE event",
  },
  services: {
    incident: "sre-platform",
    critical: "argusai-api",
    high: "argusai-gateway",
    medium: "argusai-api",
    Error: "sre-platform",
    default: "sre-platform",
  },
});

function stampDomain(raw, domain) {
  return raw.map((e) => mkEvent({ ...e, domain }));
}

export const MOCK_APPLICATION_LOGS = [...stampDomain(APPLICATION_RAW, "application"), ...APP_EXTRAS];
export const MOCK_DEVOPS_LOGS = [...stampDomain(DEVOPS_RAW, "devops"), ...DEVOPS_EXTRAS];
export const MOCK_PLATFORM_LOGS = [...stampDomain(PLATFORM_RAW, "platform"), ...PLATFORM_EXTRAS];
export const MOCK_SRE_LOGS = [...stampDomain(SRE_RAW, "sre"), ...SRE_EXTRAS];

export const MOCK_OBSERVABILITY_LOGS = [
  ...MOCK_APPLICATION_LOGS,
  ...MOCK_DEVOPS_LOGS,
  ...MOCK_PLATFORM_LOGS,
  ...MOCK_SRE_LOGS,
];

/** Backward-compatible export — full corpus */
export const MOCK_APP_LOGS = MOCK_OBSERVABILITY_LOGS;

export const LOG_LEVELS = ["ERROR", "WARN", "INFO", "DEBUG"];

export const DOMAIN_FIELD_TEMPLATES = {
  application: [
    { field: "tag", values: [{ value: "api", count: 1200 }, { value: "frontend", count: 680 }, { value: "worker", count: 420 }, { value: "gateway", count: 380 }, { value: "postgres", count: 290 }] },
    { field: "level", values: [{ value: "INFO", count: 2100 }, { value: "WARN", count: 420 }, { value: "ERROR", count: 280 }] },
    { field: "service", values: [{ value: "argusai-api", count: 980 }, { value: "argusai-gateway", count: 420 }, { value: "argusai-worker", count: 310 }] },
    { field: "logtype", values: [{ value: "json", count: 2400 }, { value: "apache_access", count: 180 }] },
  ],
  devops: [
    { field: "tag", values: [{ value: "deploy", count: 420 }, { value: "pipeline", count: 380 }, { value: "helm", count: 290 }, { value: "kubectl", count: 240 }] },
    { field: "level", values: [{ value: "INFO", count: 620 }, { value: "WARN", count: 120 }, { value: "ERROR", count: 86 }] },
    { field: "service", values: [{ value: "github-actions", count: 380 }, { value: "gitlab-ci", count: 220 }, { value: "helm", count: 180 }] },
    { field: "logger", values: [{ value: "github.actions.Runner", count: 240 }, { value: "helm.ReleaseManager", count: 180 }] },
  ],
  platform: [
    { field: "tag", values: [{ value: "k3s", count: 520 }, { value: "cilium", count: 380 }, { value: "gateway", count: 340 }, { value: "infisical", count: 220 }, { value: "supabase", count: 180 }, { value: "flowise", count: 160 }, { value: "n8n", count: 140 }, { value: "alloy", count: 120 }] },
    { field: "level", values: [{ value: "INFO", count: 780 }, { value: "WARN", count: 160 }, { value: "ERROR", count: 94 }] },
    { field: "service", values: [{ value: "k3s", count: 420 }, { value: "cilium", count: 280 }, { value: "envoy-gateway", count: 240 }] },
    { field: "host", values: [{ value: "wecrew-worker-01", count: 380 }, { value: "wecrew-worker-02", count: 340 }] },
  ],
  sre: [
    { field: "severity", values: [{ value: "critical", count: 42 }, { value: "high", count: 68 }, { value: "medium", count: 94 }] },
    { field: "level", values: [{ value: "ERROR", count: 110 }, { value: "WARN", count: 94 }] },
    { field: "tag", values: [{ value: "incident", count: 180 }] },
    { field: "service", values: [{ value: "argusai-api", count: 82 }, { value: "argusai-gateway", count: 54 }, { value: "sre-platform", count: 44 }] },
  ],
};

export const DOMAIN_QUERY_TABS = {
  application: [
    { id: "all", label: "All Events", query: "" },
    { id: "errors", label: "Server Errors", query: "level:ERROR" },
    { id: "sql", label: "SQL Logs", query: "tag:postgres" },
  ],
  devops: [
    { id: "all", label: "All CI/CD", query: "" },
    { id: "deploy-fail", label: "Deploy Failures", query: "level:ERROR tag:deploy" },
    { id: "helm", label: "Helm / K8s", query: "tag:helm OR tag:kubectl" },
  ],
  platform: [
    { id: "all", label: "All Platform", query: "" },
    { id: "k3s", label: "K3s / Cilium", query: "tag:k3s OR tag:cilium" },
    { id: "stack", label: "Wecrew Platform Stack", query: "tag:infisical OR tag:supabase OR tag:flowise OR tag:n8n OR tag:alloy" },
  ],
  sre: [
    { id: "incidents", label: "Incidents", query: "tag:incident" },
    { id: "burn", label: "Error Budget", query: "burn OR budget OR SLO" },
    { id: "errors", label: "ERROR / WARN", query: "level:ERROR OR level:WARN" },
  ],
};

export const TIME_RANGES = [
  { id: "15m", label: "Last 15 minutes" },
  { id: "1h", label: "Last 1 hour" },
  { id: "6h", label: "Last 6 hours" },
  { id: "24h", label: "Last 24 hours" },
];

function makeBuckets(seriesKeys, seed) {
  const labels = ["16:40", "16:45", "16:50", "16:55", "17:00", "17:05", "17:10", "17:15", "17:20", "17:25", "17:30", "17:35", "17:40"];
  return labels.map((label, i) => {
    const b = { label };
    seriesKeys.forEach((key, ki) => {
      b[key] = Math.round(seed[ki] * (0.6 + (i / labels.length) * 0.5) + i * (ki + 2));
    });
    return b;
  });
}

export const DOMAIN_TIMELINE = {
  application: {
    seriesKeys: ["api", "frontend", "postgres", "worker", "Error"],
    buckets: makeBuckets(["api", "frontend", "postgres", "worker", "Error"], [42, 28, 18, 14, 8]),
  },
  devops: {
    seriesKeys: ["deploy", "pipeline", "helm", "kubectl", "Error"],
    buckets: makeBuckets(["deploy", "pipeline", "helm", "kubectl", "Error"], [22, 18, 14, 12, 6]),
  },
  platform: {
    seriesKeys: ["k3s", "cilium", "gateway", "infisical", "alloy", "Error"],
    buckets: makeBuckets(["k3s", "cilium", "gateway", "infisical", "alloy", "Error"], [32, 24, 20, 16, 12, 5]),
  },
  sre: {
    seriesKeys: ["critical", "high", "medium", "incident", "Error"],
    buckets: makeBuckets(["critical", "high", "medium", "incident", "Error"], [8, 12, 16, 10, 6]),
  },
};

export const LOG_VOLUME_BUCKETS = DOMAIN_TIMELINE.application.buckets;

export function getTimelineForDomain(domain = "application") {
  const cfg = DOMAIN_TIMELINE[domain] || DOMAIN_TIMELINE.application;
  const colors = DOMAIN_COLORS[domain] || DOMAIN_COLORS.application;
  return {
    buckets: cfg.buckets,
    series: cfg.seriesKeys.map((key) => ({
      key,
      label: key,
      color: colors[key] || "#2563EB",
    })),
  };
}

export const TIMELINE_SERIES = getTimelineForDomain("application").series;

export const FIELD_EXPLORER = DOMAIN_FIELD_TEMPLATES.application;

export const QUERY_TABS = DOMAIN_QUERY_TABS.application;

export function filterLogsByDomain(logs, domain) {
  if (!domain || domain === "all") return logs;
  if (domain === "sre") {
    return logs.filter(
      (log) =>
        log.domain === "sre" ||
        log.level === "ERROR" ||
        log.level === "WARN" ||
        log.tag === "incident" ||
        log.incident_id,
    );
  }
  return logs.filter((log) => log.domain === domain);
}

export function filterLogs(logs, { search = "", levels = LOG_LEVELS, fieldFilters = {} } = {}) {
  const q = search.trim().toLowerCase();
  return logs.filter((log) => {
    if (levels?.length && !levels.includes(log.level)) return false;
    for (const [field, value] of Object.entries(fieldFilters)) {
      if (value && String(log[field] ?? "") !== String(value)) return false;
    }
    if (!q) return true;
    const tagMatch = q.match(/tag:([^\s]+)/i);
    const levelMatch = q.match(/level:([^\s]+)/i);
    if (tagMatch && log.tag?.toLowerCase() !== tagMatch[1].toLowerCase()) return false;
    if (levelMatch && log.level?.toLowerCase() !== levelMatch[1].toLowerCase()) return false;
    const free = q
      .replace(/tag:[^\s]+/gi, "")
      .replace(/level:[^\s]+/gi, "")
      .replace(/\bor\b/gi, "")
      .replace(/\band\b/gi, "")
      .trim();
    if (!free) return true;
    const haystack = [
      log.message,
      log.logger,
      log.thread,
      log.service,
      log.tag,
      log.level,
      log.host,
      log.pod,
      log.trace_id,
      log.incident_id,
      log.severity,
      String(log.status_code ?? ""),
    ]
      .join(" ")
      .toLowerCase();
    const terms = free.split(/\s+/).filter(Boolean);
    return terms.every((term) => haystack.includes(term));
  });
}

export function computeFieldExplorer(logs, template = FIELD_EXPLORER) {
  if (!Array.isArray(template)) return [];
  return template.map((fieldDef) => {
    const counts = {};
    for (const log of logs) {
      const val = log[fieldDef.field];
      if (val != null && val !== "") {
        counts[String(val)] = (counts[String(val)] || 0) + 1;
      }
    }
    const values = Object.entries(counts)
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => b.count - a.count);
    const fallback = Array.isArray(fieldDef.values) ? fieldDef.values.slice(0, 4) : [];
    return { field: fieldDef.field, values: values.length ? values : fallback };
  });
}

export function totalTimelineEvents(buckets, seriesKeys) {
  const keys = seriesKeys || buckets[0] ? Object.keys(buckets[0]).filter((k) => k !== "label") : [];
  return buckets.reduce((sum, b) => sum + keys.reduce((s, k) => s + (b[k] || 0), 0), 0);
}

export function computeLogStats(logs) {
  const total = logs.length;
  if (total === 0) return { eventsPerMin: 0, errorPct: 0, topServices: [] };
  const errors = logs.filter((l) => l.level === "ERROR").length;
  const svcCounts = {};
  for (const log of logs) {
    const key = log.service || log.tag;
    svcCounts[key] = (svcCounts[key] || 0) + 1;
  }
  const topServices = Object.entries(svcCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([name, count]) => ({ name, count }));
  return {
    eventsPerMin: Math.round((total / 60) * 10) / 10,
    errorPct: Math.round((errors / total) * 1000) / 10,
    topServices,
  };
}

export function computeSreSummary(logs) {
  const scoped = filterLogsByDomain(logs, "sre");
  const errors = scoped.filter((l) => l.level === "ERROR").length;
  const warns = scoped.filter((l) => l.level === "WARN").length;
  const total = scoped.length || 1;
  const errorRate = Math.round((errors / total) * 1000) / 10;
  const deploys = logs.filter((l) => l.domain === "devops" && l.tag === "deploy" && l.level === "INFO").length;
  const failing = {};
  for (const log of scoped.filter((l) => l.level === "ERROR")) {
    failing[log.service || log.tag] = (failing[log.service || log.tag] || 0) + 1;
  }
  const failingServices = Object.entries(failing)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4)
    .map(([name, count]) => ({ name, count }));
  const severityMix = { critical: 0, high: 0, medium: 0 };
  for (const log of scoped) {
    if (log.severity && severityMix[log.severity] != null) severityMix[log.severity] += 1;
  }
  const incidents = scoped
    .filter((l) => l.incident_id)
    .reduce((acc, l) => {
      if (!acc.find((x) => x.id === l.incident_id)) {
        acc.push({ id: l.incident_id, title: l.message.slice(0, 64), severity: l.severity || "medium" });
      }
      return acc;
    }, [])
    .slice(0, 5);
  return {
    errorRate,
    warnCount: warns,
    deploysLast24h: Math.max(deploys, 6),
    failingServices,
    severityMix,
    topIncidents: incidents,
    errorBudgetRemaining: Math.max(0, 100 - errorRate * 4.2),
    burnRate: errorRate > 5 ? "4.2x" : errorRate > 2 ? "2.1x" : "1.0x",
  };
}

export function errorRateSparkline(buckets = LOG_VOLUME_BUCKETS, seriesKeys = ["api", "frontend", "postgres", "worker", "Error"]) {
  return buckets.map((b) => {
    const total = seriesKeys.reduce((s, k) => s + (b[k] || 0), 0);
    const err = b.Error || 0;
    return total ? Math.round((err / total) * 100) : 0;
  });
}

export function crossDomainErrorSparkline() {
  const keys = ["Error"];
  const labels = DOMAIN_TIMELINE.application.buckets.map((b) => b.label);
  return labels.map((label, i) => {
    let total = 0;
    let err = 0;
    for (const domain of Object.keys(DOMAIN_TIMELINE)) {
      const b = DOMAIN_TIMELINE[domain].buckets[i];
      if (!b) continue;
      const series = DOMAIN_TIMELINE[domain].seriesKeys;
      total += series.reduce((s, k) => s + (b[k] || 0), 0);
      err += b.Error || 0;
    }
    return total ? Math.round((err / total) * 100) : 0;
  });
}

export function resolveLogsDomainFromQuery(query) {
  const q = query.toLowerCase();
  if (/\b(platform|k3s|cilium|infisical|supabase|flowise|alloy|wecrew)\b/.test(q)) return "platform";
  if (/\b(devops|ci\/cd|pipeline|deploy|helm|kubectl|github actions|gitlab)\b/.test(q)) return "devops";
  if (/\b(sre|error budget|burn rate|incident|on-call|slo)\b/.test(q)) return "sre";
  if (/\b(app(lication)? logs?|api logs?|frontend logs?|worker logs?)\b/.test(q)) return "application";
  return null;
}

export function persistLogsDomain(domain) {
  if (!domain) return;
  try {
    localStorage.setItem(LOGS_DOMAIN_KEY, domain);
  } catch {
    /* ignore */
  }
}

export function readLogsDomain() {
  try {
    const v = localStorage.getItem(LOGS_DOMAIN_KEY);
    return OBS_DOMAINS.some((d) => d.id === v) ? v : "application";
  } catch {
    return "application";
  }
}

export { DOMAIN_COLORS as TAG_COLORS };
